import React from "react";
import { Helmet } from "react-helmet-async";

const SITE = "BCPeabody";
const LEGAL = "BCPeabody Construction Services, Inc.";
const BASE = "https://bcpeabody.com";
const LOGO = `${BASE}/logo192.png`;
const DEFAULT_IMG = `${BASE}/og-image.png`;

const AREA = [
  "Tampa Bay", "Hillsborough County", "Pinellas County", "Pasco County",
  "Manatee County", "Sarasota County", "Florida",
];

export default function SEO({ title, description, path = "/", image, type = "website", noindex = false }) {
  // If the provided title already contains "BCPeabody", use it as-is (prevents
  // "Foo | BCPeabody | BCPeabody" duplication for titles that spell out the brand).
  const hasBrand = title && /BCPeabody/i.test(title);
  const fullTitle = title
    ? (hasBrand ? title : `${title} | ${SITE}`)
    : "Tampa General Contractor | Custom Homes | BCPeabody";
  const desc = description || "Family-owned, engineer-led Tampa general contractor since 2006. Custom homes, renovations, kitchens and baths, commercial and federal construction.";
  const url = `${BASE}${path}`;
  const img = image || DEFAULT_IMG;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:site_name" content={SITE} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
    </Helmet>
  );
}

export function LocalBusinessSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": ["GeneralContractor", "HomeAndConstructionBusiness"],
    "name": SITE,
    "legalName": LEGAL,
    "description": "Family-owned Tampa general contractor since 2006. Custom homes, renovations, kitchens and baths, commercial and federal construction, FEMA Public Assistance consulting and expert witness.",
    "url": BASE,
    "logo": LOGO,
    "image": DEFAULT_IMG,
    "telephone": "+1-813-919-5548",
    "email": "kimberlycarpenter@bcpeabody.com",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Tampa",
      "addressRegion": "FL",
      "postalCode": "33607",
      "addressCountry": "US",
    },
    "areaServed": AREA,
    "foundingDate": "2006",
    "founder": {
      "@type": "Person",
      "name": "Robert M. Carpenter",
      "alternateName": "Bob Carpenter",
      "jobTitle": "Founder, President & CEO",
    },
    "employee": [{
      "@type": "Person",
      "name": "Kimberly Carpenter",
      "jobTitle": "Chief Operating Officer",
    }],
    "hasCredential": [
      { "@type": "EducationalOccupationalCredential", "credentialCategory": "license", "name": "Florida Certified General Contractor CGC1515086" },
      { "@type": "EducationalOccupationalCredential", "credentialCategory": "license", "name": "Florida Certified Plumbing Contractor CFC1429871" },
      { "@type": "EducationalOccupationalCredential", "credentialCategory": "license", "name": "Florida Professional Engineer PE 63780 (Robert M. Carpenter)" },
    ],
    "knowsAbout": [
      "custom home construction","whole-home renovation","kitchen remodeling","bathroom remodeling",
      "design-build","commercial construction","franchise build-outs","federal construction",
      "FEMA Public Assistance","hazard mitigation","construction cost estimating","construction expert witness",
    ],
    "openingHoursSpecification": [{
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
      "opens": "08:00",
      "closes": "18:00",
    }],
    "sameAs": ["https://www.linkedin.com/company/bcpeabody"],
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(data)}</script></Helmet>;
}

export function PersonSchema({ name, jobTitle, description, alumniOf = [], credentials = [] }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": name,
    "jobTitle": jobTitle,
    "description": description,
    "worksFor": { "@type": "Organization", "name": SITE, "legalName": LEGAL },
    "alumniOf": alumniOf.map((n) => ({ "@type": "EducationalOrganization", "name": n })),
    "hasCredential": credentials.map((n) => ({ "@type": "EducationalOccupationalCredential", "credentialCategory": "license", "name": n })),
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(data)}</script></Helmet>;
}

export function ServiceSchema({ serviceType, path }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": serviceType,
    "url": `${BASE}${path}`,
    "provider": { "@type": "GeneralContractor", "name": SITE, "legalName": LEGAL, "url": BASE },
    "areaServed": AREA,
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(data)}</script></Helmet>;
}

export function BreadcrumbSchema({ items }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((it, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": it.name,
      "item": it.url,
    })),
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(data)}</script></Helmet>;
}
